// src/pages/Dashboard/components/YourWeek.jsx
// STATUS: active | UPDATED: 2026-09-24
// LOG:
//   2026-09-24  New. The salesperson half of the floor board.
//
// WHAT A SALESPERSON SEES OF THE SCOREBOARD, WHICH IS THEIR OWN WEEK AND NOT
// THE RANKING.
//
// The manager gets the board on /team/. This is the same five behaviours and
// the same arithmetic, pointed at one person, with nobody to compare against.
//
// WHY THE RANKING IS WITHHELD, AND IT IS NOT SOFTNESS.
//
// The charter already says a salesperson does not see anybody else's
// performance, and `floorMetrics.js` calls a leaderboard shown to the floor "a
// leaderboard with extra steps".
//
// The sharper reason is that these five are VOLUNTARY. Practice in particular
// is the one thing in this product nobody is made to do, which is exactly what
// makes it an honest read on who is engaged. A public table turns it into a
// number to farm, and the signal is gone the same week.
//
// SILENT ON A ZERO WEEK. Somebody who has done nothing does not need a card
// telling them so on the screen they open first. Their manager can see it and
// that is the right person to raise it.
//
// It is the last thing on the dashboard on purpose. The plan for today is what
// somebody came for; this is a look back at a week they have already had.

import { useEffect, useState } from 'react'
import { Box, HStack, VStack, Text } from '@chakra-ui/react'
import { Flame } from 'lucide-react'
import { scorePerson, WEEK_DAYS } from '../../../lib/floorScore'
import { loadFloorWeek } from '../../../lib/floorWeek'

export default function YourWeek({ userId }) {
  const [mine, setMine] = useState(null)

  useEffect(() => {
    let live = true
    if (!userId) return undefined
    loadFloorWeek().then(({ counts }) => {
      if (!live) return
      setMine(scorePerson(counts?.[userId]))
    })
    return () => {
      live = false
    }
  }, [userId])

  // Nothing to celebrate and nothing to scold. Render nothing.
  if (!mine || mine.total === 0) return null

  return (
    <Box bg="surface" borderRadius="card" p={5} border="1px solid" borderColor="line">
      <HStack justify="space-between" align="baseline" mb={2} flexWrap="wrap" gap={2}>
        <HStack spacing={2}>
          <Flame size={15} aria-hidden="true" />
          <Text fontSize="sm" fontWeight={600}>
            Your last {WEEK_DAYS} days
          </Text>
        </HStack>
        <Text fontFamily="mono" fontSize="lg" fontWeight={600}>
          {mine.total}
        </Text>
      </HStack>
      <VStack align="stretch" spacing={1}>
        {mine.lines.map((l) => (
          <HStack key={l.key} justify="space-between" align="baseline">
            <Text fontSize="sm" color="inkMuted">
              {l.label}
              {l.capped && (
                <Text as="span" fontSize="xs" color="inkDim" fontFamily="mono" ml={2}>
                  {l.counted} of {l.raw} counted
                </Text>
              )}
            </Text>
            <Text fontSize="sm" fontFamily="mono" color="inkDim" whiteSpace="nowrap">
              {l.counted} &times; {l.points / l.counted}
            </Text>
          </HStack>
        ))}
      </VStack>
      <Text fontSize="xs" color="inkDim" mt={3} lineHeight={1.6} maxW="55ch">
        This counts what you chose to do about getting better, not what you sold. Nobody else sees
        your number.
      </Text>
    </Box>
  )
}
